import React from 'react';
import { Table, Tooltip, Button } from 'antd';
import * as R from 'ramda';
import moment from 'moment';
import {
  StarTwoTone,
  SmileTwoTone,
  DollarOutlined,
  LoginOutlined,
} from '@ant-design/icons';
import { IBooking, IBookingRound } from '../../types/models/lodging';

// Using the interface from the original file, maybe move to a types file later
interface IAssignment {
  id: number;
  assignment_round: number;
  unit_owner: number;
  fraction: number;
  checkin: string;
  checkout: string;
  week: number;
  preferred: boolean;
  preferred_rank: number;
  rank: number;
  price_avg: number;
  round: number;
}

interface IHouseOwner {
  id: number;
  fraction_numbers: number[];
  owner: any;
}

interface IWeekRank {
  week: number;
  rank: number;
  price_avg: number;
}

interface BookingRoundPicksTableProps {
  bookingRound: IBookingRound | undefined;
  assignments: IAssignment[];
  fractionsCount: number;
  houseOwners: IHouseOwner[];
  weekRanking: IWeekRank[];
  bookings: IBooking[];
  setCurrentOwnerEdit: (owner: any) => void;
  selectedAssignments: IAssignment[];
  setSelectedAssignments: (assignments: IAssignment[]) => void;
}

const BookingRoundPicksTable: React.FC<BookingRoundPicksTableProps> = ({
  bookingRound,
  assignments,
  fractionsCount,
  houseOwners,
  weekRanking,
  bookings,
  setCurrentOwnerEdit,
  selectedAssignments,
  setSelectedAssignments,
}) => {

  const findLongWeekend = (checkin: any, checkout: any) => {
    if (!checkin || !checkout) return false;
    const longWeekends2025 = [
      { date: '2025-01-29', holiday: 'Día de la Constitución' },
      { date: '2025-03-12', holiday: 'Natalicio de Benito Juárez' },
      { date: '2025-04-30', holiday: 'Día del Trabajo' },
      { date: '2025-09-10', holiday: 'Día de la Independencia' },
      { date: '2025-11-12', holiday: 'Día de la Revolución' }
    ];
    const start = new Date(checkin).getTime();
    const end = new Date(checkout).getTime();
    return longWeekends2025.find(holiday => {
      const holidayDate = new Date(holiday.date).getTime();
      return holidayDate >= start && holidayDate <= end;
    }) || null;
  };

  function rotateArrayLeft(arr: any[], n: number) {
    const rotationIndex = n % arr.length;
    const leftPart = arr.slice(rotationIndex);
    const rightPart = arr.slice(0, rotationIndex);
    return leftPart.concat(rightPart);
  }

  const numberOfRounds = (weekRanking.length > 0 && fractionsCount > 0) ? (weekRanking.length / fractionsCount) : 1;

  if (!bookingRound || typeof bookingRound?.unit === 'number') {
    return null;
  }

  const columns = [
    { title: '', dataIndex: 'name', width: 16, fixed: 'left', render: (r: any) => <small style={{ marginLeft: 4 }}>{r}</small> },
    ...rotateArrayLeft(R.range(1, fractionsCount + 1), bookingRound.start_index - 1).map(fn => {
      const ho = houseOwners.find(ho => ho.fraction_numbers.includes(fn));
      const totalValue = (
        assignments
          .filter(assigment => assigment.fraction === fn)
          .reduce((a, b) => {
            return a + (weekRanking.find(rank => rank.week === b.week)?.price_avg || 0);
          }, 0)
          .toFixed(2)
      );
      return ({
        title: (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', paddingTop: 6 }}>
            <Tooltip
              title={(
                <div>
                  <div>{ho?.owner.full_name}</div>
                  <div>Fraction #{fn}</div>
                  <div>Total Value: <span
                    className="fw-400">${Number(totalValue).toLocaleString('en')}</span></div>
                </div>
              )}
            >
              <div style={{ letterSpacing: -0.5, fontWeight: ho?.id ? '500' : '400' }}>
                {ho?.owner.first_name.split(" ")[0] || 'Empty'} (#{fn})
              </div>
            </Tooltip>
            <Button
              type="link"
              size="small"
              className="fs-2 cursor-pointer mb-xs"
              onClick={() => setCurrentOwnerEdit(ho)}
            >
              {ho?.id ? (<><LoginOutlined /> Selection</>) : '-'}
            </Button>
          </div>
        ),
        render: (rr: any) => {
          const assignment = assignments.find(a => a.fraction === fn && a.round === rr.round);
          if (!assignment) return null; // If no assignment, render an empty cell

          const weekRankPos = weekRanking.find(wr => wr.week === assignment?.week);
          const longWeekend = findLongWeekend(assignment?.checkin, assignment?.checkout);
          const isSelected = selectedAssignments.some(sa => sa.id === assignment.id);

          const handleSelect = () => {
            if (bookingRound.closed) return;

            if (isSelected) {
              setSelectedAssignments(selectedAssignments.filter(sa => sa.id !== assignment.id));
            } else {
              if (selectedAssignments.length < 2) {
                setSelectedAssignments([...selectedAssignments, assignment]);
              }
            }
          };

          if (bookingRound.closed) {
            return (
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                opacity: 0.75,
                padding: '12px 6px',
                cursor: 'not-allowed'
              }}>
                <div className="text-center fs-2 fw-500" style={{ letterSpacing: -0.5 }}>
                  {moment(assignment.checkin).format('MMM D')}-{moment(assignment.checkin).format('MMM') === moment(assignment.checkout).format('MMM') ? moment(assignment.checkout).format('D') : moment(assignment.checkout).format('MMM D')}
                </div>
                <div className="fs-1-5" style={{
                  marginTop: 2,
                  display: 'flex',
                  gap: 12,
                  alignItems: 'center',
                  letterSpacing: -0.5
                }}>
                  <span><DollarOutlined style={{ fontSize: 12 }} /> #{weekRankPos?.rank}</span>
                  {assignment?.preferred && <SmileTwoTone style={{ fontSize: 14 }} />}
                  {!!longWeekend && <StarTwoTone twoToneColor="orange" style={{ fontSize: 14 }} />}
                </div>
              </div>
            );
          } else {
            return (
              <Tooltip
                title={(
                  <ul style={{ padding: '0 0 0 12px', margin: 0 }}>
                    {assignment?.preferred && (<li>Owner's Preferred <SmileTwoTone /></li>)}
                    {!!longWeekend && (
                      <li>{longWeekend.date} {longWeekend.holiday} <StarTwoTone twoToneColor="orange"
                                                                                style={{ fontSize: 14 }} />
                      </li>)}
                    <li>#{weekRankPos?.rank} in Price Rank <DollarOutlined /></li>
                    <li>${assignment?.price_avg?.toLocaleString()} Avg per night</li>
                    <li>Week {assignment?.week} of the year</li>
                  </ul>
                )}
                style={{ textAlign: 'center' }}
              >
                <div
                  onClick={handleSelect}
                  style={{
                    cursor: 'pointer',
                    backgroundColor: isSelected ? 'rgba(76,189,241,0.2)' : 'transparent',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    padding: '12px 6px'
                  }}
                >
                  <div className="text-center fs-2 fw-500" style={{ letterSpacing: -0.5 }}>
                    {moment(assignment.checkin).format('MMM D')}-{moment(assignment.checkin).format('MMM') === moment(assignment.checkout).format('MMM') ? moment(assignment.checkout).format('D') : moment(assignment.checkout).format('MMM D')}
                  </div>
                  <div className="fs-1-5" style={{
                    marginTop: 2,
                    display: 'flex',
                    gap: 12,
                    alignItems: 'center',
                    letterSpacing: -0.5
                  }}>
                    <span><DollarOutlined style={{ fontSize: 12 }} /> #{weekRankPos?.rank}</span>
                    {assignment?.preferred && <SmileTwoTone style={{ fontSize: 14 }} />}
                    {!!longWeekend && <StarTwoTone twoToneColor="orange" style={{ fontSize: 14 }} />}
                  </div>
                </div>
              </Tooltip>
            )
          }
        },
        width: 140
      });
    })
  ];

  return (
    <>
      <Table
        bordered={false}
        columns={columns as any}
        dataSource={R.range(1, numberOfRounds).map(roundNum => ({ name: roundNum.toString(), round: roundNum }))}
        pagination={false}
        size="middle"
        style={{ width: '100%' }}
      />
      <div style={{ display: 'flex', flexDirection: 'row', gap: 16, alignItems: 'center', padding: '6px 12px' }}>
        <div className="fs-2-66 fw-500">Legend:</div>
        <div>
          <DollarOutlined /> Price Rank
        </div>
        <div>
          <SmileTwoTone /> Preferred week
        </div>
        <div>
          <StarTwoTone twoToneColor="orange" /> Long weekend
        </div>
      </div>
    </>
  );
};

export default BookingRoundPicksTable;
